import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useTranslation } from 'next-i18next'
import React from 'react'

import ProductInfoPriceDS from '@src/components/blocks/product-info-price-ds/product-info-price-ds'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { IconButton } from '@src/quince-components/src/atoms/icon-button'
import { Link } from '@src/quince-components/src/atoms/link'
import { ProductTraditionalDiscountMessage } from '@src/quince-components/src/atoms/productTraditionalDiscountMessage'
import { PurchaseType } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import usePdpPriceRedesignExperiment from '@src/quince-site-common/src/hooks/use-pdp-price-redesign-experiment'
import Heart from '@src/quince-site-common/src/organisms/appLayout/molecules/header/icons/heart.inline.svg'

import * as styles from './product-info-ds.styles'
import ProductInfoReviewSummaryDS from './product-info-review-summary-ds'

export interface ProductInfoDSProps {
  product?: Product
  productInfoHeaderClassName?: string
  productInfoHeaderTextClassName?: string
  productTitle: string
  formattedSalePrice?: string
  hideSalePrice?: boolean
  formattedTraditionalPrice?: string
  isWishListed?: boolean
  className?: string
  avgRating?: number
  reviewCount?: number
  onWishListToggle?: (e: React.MouseEvent<HTMLElement>) => void
  hideWishListIcon?: boolean
  hideReviewSummary?: boolean
  onReviewSummaryClick?: (e: React.MouseEvent<HTMLElement>) => void
  savingPercentage?: number
  brandName?: string
  brandLink?: string
  traditionalDiscountMessage?: string
  formattedFestivalOfferPrice?: string
  hideReviewCount?: boolean
  hideReviewLabel?: boolean
  productInfoReviewClassName?: string
  showDiscount?: boolean
  festivalPricePerUnit?: number
  subscriptionOfferPricePerUnit?: number
  pricePerServing?: number
  traditionalPricePerServing?: number
  hideSavings?: boolean
  showNewPdpDesignV2?: boolean
  isBundle?: boolean
  isFinalSaleProduct?: boolean
  isB2BFinalSaleProduct?: boolean
  formattedSubscriptionPrice?: string
  purchaseOption: PurchaseType
  updatedDesignForV2?: boolean
  containerRef?: React.RefObject<HTMLDivElement>
  isVariantThirdParty?: boolean
  selectedOptions?: ProductVariantOption[]
}

const ProductInfoDS = ({
  product,
  productInfoHeaderClassName,
  productInfoHeaderTextClassName,
  productTitle,
  formattedSalePrice,
  hideSalePrice = false,
  formattedTraditionalPrice,
  isWishListed = false,
  className,
  avgRating,
  reviewCount,
  onWishListToggle,
  hideWishListIcon,
  hideReviewSummary,
  onReviewSummaryClick,
  savingPercentage,
  brandName,
  brandLink,
  traditionalDiscountMessage,
  formattedFestivalOfferPrice,
  hideReviewCount,
  hideReviewLabel,
  productInfoReviewClassName,
  showDiscount,
  festivalPricePerUnit,
  subscriptionOfferPricePerUnit,
  pricePerServing,
  traditionalPricePerServing,
  hideSavings,
  showNewPdpDesignV2,
  isBundle,
  isFinalSaleProduct,
  isB2BFinalSaleProduct,
  formattedSubscriptionPrice,
  purchaseOption,
  updatedDesignForV2 = false,
  containerRef,
  isVariantThirdParty,
  selectedOptions,
}: ProductInfoDSProps) => {
  const { t } = useTranslation('common')
  const pdpPriceRedesign = usePdpPriceRedesignExperiment()
  const STYLE_BLACK = 'black'
  const STYLE_WHITE = 'white'
  const STYLE_WIDTH = '20px'
  const STYLE_HEIGHT = '18px'
  const innerStyle = {
    // eslint-disable-next-line i18next/no-literal-string
    fill: isWishListed ? STYLE_BLACK : STYLE_WHITE,
    // eslint-disable-next-line i18next/no-literal-string
    width: STYLE_WIDTH,
    // eslint-disable-next-line i18next/no-literal-string
    height: STYLE_HEIGHT,
  }
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const reviewSummaryNotVisible = hideReviewSummary || !avgRating || Number(reviewCount) <= 0
  return (
    <div
      ref={containerRef}
      data-testid="product-info"
      data-show-new-pdp-design-v2={showNewPdpDesignV2?.toString()}
      data-is-bundle={isBundle?.toString()}
      className={cx(
        styles.containerCss,
        className,
        updatedDesignForV2 && styles.containerUpdatedDesignV2Css,
        isVariantThirdParty && styles.containerThirdPartyCss,
        isBundle && styles.containerNewDesignV2BundleCss,
      )}
    >
      {brandName &&
        (brandLink ? (
          <Link
            href={brandLink}
            className={cx(styles.containerBrandNameCss, showNewPdpDesignV2 && styles.containerBrandNameNewDesignV2Css)}
          >
            {brandName}
          </Link>
        ) : (
          <div
            className={cx(
              styles.containerBrandNameWithoutLinkCss,
              showNewPdpDesignV2 && styles.containerBrandNameWithoutLinkNewDesignV2Css,
            )}
          >
            {brandName}
          </div>
        ))}
      <div
        className={cx(
          styles.containerHeaderCss,
          productInfoHeaderClassName,
          showNewPdpDesignV2 && styles.containerHeaderNewDesignV2Css,
          updatedDesignForV2 && styles.containerHeaderUpdatedDesignV2Css,
        )}
      >
        <h1
          className={cx(
            styles.containerHeaderTextCss,
            styles.containerHeaderTextAdjustedPaddingCss,
            productInfoHeaderTextClassName,
            showNewPdpDesignV2 && styles.containerHeaderTextNewDesignV2Css,
            reviewSummaryNotVisible && styles.containerHeaderTextNewDesignV2ReviewSummaryNotVisibleCss,
          )}
        >
          {productTitle}
        </h1>
        {!hideWishListIcon && (
          <IconButton
            className={styles.containerHeaderIconCss}
            iconSvg={Heart}
            innerStyle={innerStyle}
            onClick={onWishListToggle}
            ariaLabel={t('mark_product_as_favourite')}
            dataTestId={isWishListed ? TEST_LOCATORS.REMOVE_FROM_WISHLIST_BUTTON : TEST_LOCATORS.ADD_TO_WISHLIST_BUTTON}
          />
        )}
        {showNewPdpDesignV2 ? (
          <ProductInfoReviewSummaryDS
            avgRating={avgRating}
            reviewCount={reviewCount}
            onReviewSummaryClick={onReviewSummaryClick}
            hideReviewCount={hideReviewCount}
            hideReviewLabel={true}
            productInfoReviewClassName={productInfoReviewClassName}
            hideReviewSummary={hideReviewSummary}
            showNewPdpDesignV2={showNewPdpDesignV2}
            updatedDesignForV2={updatedDesignForV2}
          />
        ) : null}
      </div>
      {!showNewPdpDesignV2 && (
        <ProductInfoReviewSummaryDS
          avgRating={avgRating}
          reviewCount={reviewCount}
          onReviewSummaryClick={onReviewSummaryClick}
          hideReviewCount={hideReviewCount}
          hideReviewLabel={hideReviewLabel}
          productInfoReviewClassName={productInfoReviewClassName}
          hideReviewSummary={hideReviewSummary}
          updatedDesignForV2={updatedDesignForV2}
        />
      )}
      {!hideSalePrice && !!formattedSalePrice && (
        <ProductInfoPriceDS
          product={product}
          formattedSubscriptionPrice={formattedSubscriptionPrice}
          formattedSalePrice={formattedSalePrice}
          formattedTraditionalPrice={formattedTraditionalPrice}
          savingPercentage={savingPercentage}
          formattedFestivalOfferPrice={formattedFestivalOfferPrice}
          showDiscount={showDiscount}
          subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
          pricePerServing={pricePerServing}
          traditionalPricePerServing={traditionalPricePerServing}
          festivalPricePerUnit={festivalPricePerUnit}
          hideSavings={hideSavings}
          showNewPdpDesignV2={showNewPdpDesignV2}
          isFinalSaleProduct={isFinalSaleProduct}
          isB2BFinalSaleProduct={isB2BFinalSaleProduct}
          purchaseOption={purchaseOption}
          updatedDesignForV2={updatedDesignForV2}
          isVariantThirdParty={isVariantThirdParty}
          selectedOptions={selectedOptions}
          brandName={brandName}
          pdpPriceRedesign={pdpPriceRedesign}
        />
      )}

      <ProductTraditionalDiscountMessage
        formattedTraditionalPrice={formattedTraditionalPrice}
        traditionalDiscountMessage={traditionalDiscountMessage}
      />
    </div>
  )
}

export default ProductInfoDS
